import type { ComponentType } from 'react';
import { ShopliveCustomHeader } from './component/customHeader/ShopliveCustomHeader';

/**
 * WebViewScreen 의 헤더를 pathname 으로 결정한다.
 *
 * WebViewScreen 은 기본적으로 헤더를 숨기고(=웹뷰가 전체 화면), 아래 목록에
 * 매칭되는 pathname 으로 진입한 경우에만 지정된 헤더를 그린다.
 *
 * - `{ type: 'default' }`  : 기본 헤더(StackScreen 의 title + 검색/장바구니 아이콘).
 * - `{ type: 'custom' }`   : 해당 경로 전용 커스텀 헤더 컴포넌트.
 *
 * pathname 의 prefix(startsWith) 매칭이므로 하위 경로까지 포함된다.
 * (예: '/agreement' → '/agreement/terms-of-use'). 더 구체적인 경로를 앞에 둔다.
 */
export type WebViewHeader =
  | { type: 'default' }
  | { type: 'custom'; Component: ComponentType };

const HEADER_ROUTES: ReadonlyArray<{
  prefix: string;
  header: WebViewHeader;
}> = [
  // shoplive 편성표: ABLY LIVE 로고가 들어간 전용 커스텀 헤더
  {
    prefix: '/shoplive/calendar',
    header: { type: 'custom', Component: ShopliveCustomHeader },
  },
  { prefix: '/agreement', header: { type: 'default' } }, // 이용약관 / 개인정보 처리방침
  { prefix: '/membership', header: { type: 'default' } }, // 이용약관 / 개인정보 처리방침
  { prefix: '/help', header: { type: 'default' } }, // 이용약관 / 개인정보 처리방침
  { prefix: '/style', header: { type: 'default' } }, // 이용약관 / 개인정보 처리방침
  { prefix: '/youth-policy', header: { type: 'default' } }, // 이용약관 / 개인정보 처리방침
  { prefix: '/oss-licenses', header: { type: 'default' } }, // 이용약관 / 개인정보 처리방침
  { prefix: '/events', header: { type: 'default' } }, // events
  { prefix: '/goods', header: { type: 'default' } }, // goods
];

/**
 * 주어진 url 의 pathname 에 매칭되는 헤더 설정을 반환. 매칭이 없으면 null(=헤더 숨김).
 */
export const resolveWebViewHeader = (
  url: string | undefined,
): WebViewHeader | null => {
  if (url == null) return null;
  try {
    const { pathname } = new URL(url);
    return (
      HEADER_ROUTES.find(({ prefix }) => pathname.startsWith(prefix))?.header ??
      null
    );
  } catch {
    return null;
  }
};
